/**
 * Etap/standard wykonczenia budowy ktorym zainteresowany jest klient.
 *
 * Slug w bazie (text), label po polsku w UI. Pole opcjonalne — klient
 * czesto na etapie pierwszego kontaktu nie wie czego potrzebuje.
 */
export const BUILD_STAGES = [
  'stan_zero',
  'stan_surowy_otwarty',
  'stan_surowy_zamkniety',
  'stan_deweloperski',
  'pod_klucz',
  'inny',
] as const;

export type BuildStage = (typeof BUILD_STAGES)[number];

export const BUILD_STAGE_LABELS: Record<BuildStage, string> = {
  stan_zero: 'Stan zero (fundamenty)',
  stan_surowy_otwarty: 'Stan surowy otwarty (SSO)',
  stan_surowy_zamkniety: 'Stan surowy zamknięty (SSZ)',
  stan_deweloperski: 'Stan deweloperski',
  pod_klucz: 'Pod klucz',
  inny: 'Inny / nie wiem',
};

/** Krotsze etykiety dla widoku karty/listy gdzie miejsce jest ograniczone. */
export const BUILD_STAGE_LABELS_SHORT: Record<BuildStage, string> = {
  stan_zero: 'Stan zero',
  stan_surowy_otwarty: 'SSO',
  stan_surowy_zamkniety: 'SSZ',
  stan_deweloperski: 'Deweloperski',
  pod_klucz: 'Pod klucz',
  inny: 'Inny',
};

/**
 * Normalizacja stringa do porownan: lower + usuniecie polskich znakow +
 * tylko alfanumeryczne. "Stan surowy zamkniety" / "stan_surowy_zamkniety" /
 * "Stan surowy zamknięty (SSZ)" → "stansurowyzamkniety".
 */
const PL_CHARS: Record<string, string> = {
  ą: 'a',
  ć: 'c',
  ę: 'e',
  ł: 'l',
  ń: 'n',
  ó: 'o',
  ś: 's',
  ź: 'z',
  ż: 'z',
};

function normalizeStandard(value: string): string {
  return value
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (ch) => PL_CHARS[ch] ?? ch)
    .replace(/[^a-z0-9]/g, '');
}

/** Lookup: znormalizowany klucz/label/short → BuildStage. */
const STANDARD_LOOKUP: Record<string, BuildStage> = (() => {
  const map: Record<string, BuildStage> = {};
  for (const key of BUILD_STAGES) {
    map[normalizeStandard(key)] = key;
    map[normalizeStandard(BUILD_STAGE_LABELS[key])] = key;
    map[normalizeStandard(BUILD_STAGE_LABELS_SHORT[key])] = key;
  }
  return map;
})();

/**
 * Mapuje dowolny zapis standardu (klucz enuma z kolumny build_stage LUB
 * label z kalkulatora w source_metadata, z polskimi znakami) na kanoniczny
 * BuildStage. null gdy nie rozpoznano. Rozwiazuje rozbieznosc opisow
 * standardow miedzy build_stage a metadanymi.
 */
export function resolveBuildStage(value: string | null | undefined): BuildStage | null {
  if (!value) return null;
  return STANDARD_LOOKUP[normalizeStandard(value)] ?? null;
}

/**
 * Kolejnosc etapow w harmonogramie platnosci:
 * Stan zero (1) → SSO (2) → SSZ (3) → SD (4). Pomijamy pod_klucz/inny
 * jako osobne etapy w harmonogramie — wpadaja jako "Inne prace" gdyby
 * to byly jedyne etapy w ofercie.
 */
export const PAYMENT_STAGE_ORDER: BuildStage[] = [
  'stan_zero',
  'stan_surowy_otwarty',
  'stan_surowy_zamkniety',
  'stan_deweloperski',
  'pod_klucz',
  'inny',
];
